import { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { OPERATION_FIND_BY_CORRELATION, OPERATION_FIND_COUPON_STANDARD } from './operations-gql/queries'
import generatePdfContentCode from './Actions/PrintCode'
import generatePdfContentNotebook from './Actions/PrintNotebook'
import { useCouponsSelected } from './store'

const usePdf = () => {
  const { i18n, t } = useTranslation()
  const [couponsSelected] = useCouponsSelected()
  const [loadingPdfMake, setLoadingPdfMake] = useState(false)
  const [error, setError] = useState(null)

  const loadPdfMake = async () => {
    setLoadingPdfMake(true)
    try {
      const pdfMakeModule = await import('pdfmake/build/pdfmake')
      const pdfFonts = await import('pdfmake/build/vfs_fonts')
      pdfMakeModule.default.vfs = pdfFonts.default.pdfMake?.vfs
      return pdfMakeModule.default
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setLoadingPdfMake(false)
    }
  }

  const [findCouponByCorrelationId, { loading: loadingQuery }] = useLazyQuery(OPERATION_FIND_BY_CORRELATION, {
    fetchPolicy: 'no-cache',
    onError: err => setError(err)
  })
  const [findCouponById, { loading: loading }] = useLazyQuery(OPERATION_FIND_COUPON_STANDARD, {
    fetchPolicy: 'no-cache',
    onError: err => setError(err)
  })

  const handlePrintSelection = async ({ withOffer }) => {
    const pdfMake = await loadPdfMake()
    const docDefinition = generatePdfContentNotebook({ t, correlation: couponsSelected, withOffer })
    pdfMake.createPdf(docDefinition).open()
  }

  const handlePrintNotebook = async ({ coupon, withOffer }) => {
    const pdfMake = await loadPdfMake()
    const { data } = await findCouponByCorrelationId({
      variables: { findCouponByCorrelationIdId: coupon.correlation_id, langue: i18n.language }
    })

    const correlation =
      data?.findCouponByCorrelationId.filter(coupon => coupon.available === true || coupon.available_soon > 0) || []

    if (!correlation.length) {
      setError(new Error('No valid coupons found'))
      return
    }
    if (correlation.length === 1) {
      const docDefinition = generatePdfContentCode({ coupon, withOffer, t })
      pdfMake.createPdf(docDefinition).open()
    } else {
      const docDefinition = generatePdfContentNotebook({ t, correlation, withOffer })
      pdfMake.createPdf(docDefinition).open()
    }
  }

  const handlePrintCode = async ({ coupon, withOffer = true }) => {
    try {
      const pdfMake = await loadPdfMake()
      // recall cause hotline_number depends of langue
      const { data } = await findCouponById({
        variables: { id: parseInt(coupon.id), langue: i18n.language }
      })
      const docDefinition = generatePdfContentCode({ coupon: data.coupon, t, withOffer })
      pdfMake.createPdf(docDefinition).open()
    } catch (err) {
      console.log(err)
      setError(err)
    }
  }

  return {
    loadPdfMake,
    handlePrintCode,
    handlePrintNotebook,
    handlePrintSelection,
    findCouponByCorrelationId,
    loading: loadingPdfMake || loadingQuery || loading,
    error
  }
}

export default usePdf
